import React, { useCallback, useRef, useState } from 'react';
import { FaArrowLeft, FaCloudUploadAlt, FaExclamationTriangle, FaLightbulb } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES, adminPath } from '../constants/adminPortal';
import { graphqlRequest } from '../utils/graphqlRequest';
import { CREATE_REPORTED_ISSUE_MUTATION } from '../graphql/reportedIssue';
import '../styles/AdminReportIssue.css';

const ISSUE_TYPES = [
  { value: '', label: 'Select issue type' },
  { value: 'bug', label: 'Bug / Something is broken' },
  { value: 'feature', label: 'Feature request' },
  { value: 'access', label: 'Access or login issue' },
  { value: 'data', label: 'Incorrect data' },
  { value: 'performance', label: 'Performance' },
  { value: 'other', label: 'Other' },
];

const PRIORITIES = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'High' },
  { id: 'urgent', label: 'Urgent' },
];

const MIN_DESCRIPTION = 20;
const MAX_FILE_MB = 10;

const PRIORITY_TO_GQL = {
  low: 'LOW',
  medium: 'MEDIUM',
  high: 'HIGH',
  urgent: 'URGENT',
};

function ReportIssue() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [issueType, setIssueType] = useState('');
  const [relatedProject, setRelatedProject] = useState('');
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const descLen = description.trim().length;
  const descOk = descLen >= MIN_DESCRIPTION;

  const addFiles = useCallback((fileList) => {
    const next = Array.from(fileList || []);
    const valid = next.filter((f) => f.size <= MAX_FILE_MB * 1024 * 1024);
    if (valid.length < next.length) {
      setError(`Some files exceed ${MAX_FILE_MB}MB and were skipped.`);
    } else {
      setError('');
    }
    setFiles((prev) => [...prev, ...valid]);
  }, []);

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(e.dataTransfer.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const onDragLeave = () => setDragActive(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!issueType) {
      setError('Please select an issue type.');
      return;
    }
    if (!title.trim()) {
      setError('Please enter an issue title.');
      return;
    }
    if (!descOk) {
      setError(`Description must be at least ${MIN_DESCRIPTION} characters.`);
      return;
    }

    setSubmitting(true);
    try {
      const gqlPriority = PRIORITY_TO_GQL[priority];
      if (!gqlPriority) {
        setError('Invalid priority.');
        setSubmitting(false);
        return;
      }
      const data = await graphqlRequest(CREATE_REPORTED_ISSUE_MUTATION, {
        input: {
          issueType,
          relatedProject: relatedProject.trim() || null,
          title: title.trim(),
          priority: gqlPriority,
          description: description.trim(),
          attachmentFileNames: files.length ? files.map((f) => f.name) : null,
        },
      });
      const ri = data?.createReportedIssue;
      const num = ri?.issueNumber != null ? String(ri.issueNumber).padStart(3, '0') : null;
      navigate(adminPath(ADMIN_ROUTES.HELP_SUPPORT), {
        state: {
          supportBanner: num
            ? `Your issue (ISS-${num}) was submitted. We typically respond within 24 hours.`
            : 'Your issue was submitted. We typically respond within 24 hours.',
        },
      });
    } catch {
      setError('Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => navigate(-1);

  return (
    <div className="report-issue-page">
      <header className="report-issue-hero">
        <div className="report-issue-hero-inner">
          <button type="button" className="report-issue-back" onClick={goBack} aria-label="Go back">
            <FaArrowLeft />
          </button>
          <div className="report-issue-hero-icon" aria-hidden>
            <FaExclamationTriangle />
          </div>
          <div className="report-issue-hero-text">
            <h1>Report an Issue</h1>
            <p>Describe the problem you are facing and our team will help resolve it.</p>
          </div>
        </div>
      </header>

      <form className="report-issue-form-card" onSubmit={handleSubmit} noValidate>
        {error ? <div className="report-issue-banner-error">{error}</div> : null}

        <div className="report-issue-form-grid">
          <div className="report-issue-col">
            <label className="report-issue-label" htmlFor="issue-type">
              Issue Type <span className="report-issue-req">*</span>
            </label>
            <select
              id="issue-type"
              className="report-issue-input"
              value={issueType}
              onChange={(e) => setIssueType(e.target.value)}
              required
            >
              {ISSUE_TYPES.map((opt) => (
                <option key={opt.value || 'empty'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <label className="report-issue-label" htmlFor="related-project">
              Related Project
            </label>
            <input
              id="related-project"
              type="text"
              className="report-issue-input"
              placeholder="Project name or ID (optional)"
              value={relatedProject}
              onChange={(e) => setRelatedProject(e.target.value)}
            />

            <label className="report-issue-label" htmlFor="issue-title">
              Issue Title <span className="report-issue-req">*</span>
            </label>
            <input
              id="issue-title"
              type="text"
              className="report-issue-input"
              placeholder="Brief description of the issue"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />

            <div className="report-issue-label report-issue-label--inline">
              Priority Level <span className="report-issue-req">*</span>
            </div>
            <div className="report-issue-priority-row" role="group" aria-label="Priority level">
              {PRIORITIES.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`report-issue-priority-btn ${priority === p.id ? 'active' : ''}`}
                  onClick={() => setPriority(p.id)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="report-issue-col">
            <label className="report-issue-label" htmlFor="issue-desc">
              Issue Description <span className="report-issue-req">*</span>
            </label>
            <textarea
              id="issue-desc"
              className="report-issue-textarea"
              rows={8}
              placeholder="Please provide detailed information about the issue you're experiencing..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
            <p className={`report-issue-hint ${descOk ? 'ok' : ''}`}>
              Minimum {MIN_DESCRIPTION} characters ({descLen}/{MIN_DESCRIPTION}+)
            </p>

            <div className="report-issue-label">Attachments</div>
            <div
              className={`report-issue-dropzone ${dragActive ? 'active' : ''}`}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
            >
              <FaCloudUploadAlt className="report-issue-upload-icon" aria-hidden />
              <strong>Drop files here or click to upload</strong>
              <span>Images, documents, screenshots (Max {MAX_FILE_MB}MB each)</span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="report-issue-file-input"
              multiple
              accept="image/*,.pdf,.doc,.docx,.txt"
              onChange={(e) => addFiles(e.target.files)}
            />
            <button
              type="button"
              className="report-issue-browse portal-btn-primary"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              Browse Files
            </button>

            {files.length > 0 ? (
              <ul className="report-issue-file-list">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`}>
                    <span>{f.name}</span>
                    <button type="button" onClick={() => removeFile(i)}>
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>

        <div className="report-issue-actions">
          <button type="button" className="report-issue-cancel portal-btn-secondary" onClick={goBack}>
            Cancel
          </button>
          <button type="submit" className="report-issue-submit portal-btn-primary" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit Issue'}
          </button>
        </div>
      </form>

      <aside className="report-issue-tips">
        <div className="report-issue-tips-head">
          <FaLightbulb className="report-issue-tips-icon" aria-hidden />
          <h2>Tips for reporting issues</h2>
        </div>
        <ul>
          <li>Be specific about when the issue occurred and what you were trying to do</li>
          <li>Include any error messages you received</li>
          <li>Attach screenshots if possible to help our team understand the problem</li>
          <li>Our support team typically responds within 24 hours</li>
        </ul>
      </aside>
    </div>
  );
}

export default ReportIssue;
